import styled from '@emotion/styled';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';
import { toUniversalLink, withPresentOverWebView } from '../../utils/deeplinkMapper';
import { trackContentClicked, type ContentType } from '../../services/analytics.service';

interface ContentRecommendation {
  id: string;
  content_id: string;
  content_type: string;
  title: string;
  subtitle?: string;
  image?: string;
  url: string;
  isPlayable: boolean;
  duration?: number;
  similarity?: number;
  tags?: string[];
  recId?: number;
}

interface ContentRecommendationsProps {
  recommendations: ContentRecommendation[];
}

const Container = styled.div`
  width: 100%;
  margin: ${theme.spacing.md} 0;
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  overflow-x: auto;
  padding: 0 ${theme.spacing.md};
  padding-bottom: ${theme.spacing.xs};
  scroll-behavior: smooth;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const Card = styled.a`
  position: relative;
  flex-shrink: 0;
  width: 160px;
  text-decoration: none;
  cursor: pointer;
  transition: transform ${theme.transitions.fast};

  &:active {
    transform: scale(0.98);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: ${theme.spacing.sm};
  background: linear-gradient(135deg, #5C4D7D 0%, #834CC9 50%, #2D3A8C 100%);

  [data-theme="light"] & {
    background: linear-gradient(135deg, #EEE4FB 0%, #D4C1F2 50%, #C8D2F1 100%);
    border: 1px solid var(--color-border-subtle);
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 8px 0 8px 14px;
    border-color: transparent transparent transparent #1A1A2E;
    margin-left: 2px;
  }
`;

const Badge = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  left: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(10px);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: ${theme.typography.fontFamily.primary};

  [data-theme="light"] & {
    background: rgba(255, 255, 255, 0.85);
    color: #1A1A2E;
  }
`;

const DurationBadge = styled.div`
  position: absolute;
  bottom: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  padding: 3px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

const CardSubtitle = styled.div`
  font-size: 12px;
  color: var(--color-text-secondary);
  font-family: ${theme.typography.fontFamily.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
`;

const AMPLITUDE_CONTENT_TYPES: ContentType[] = ['meditation', 'story', 'music', 'sound', 'blog'];

// Fire-and-forget click signal. Uses sendBeacon so the request survives the
// WebView navigating away to the Meditopia player (same drop class that broke
// the SSE persistence). Also mirrors to Amplitude for parity.
function recordRecommendationClick(item: ContentRecommendation): void {
  try {
    const amplitudeType = AMPLITUDE_CONTENT_TYPES.includes(item.content_type as ContentType)
      ? (item.content_type as ContentType)
      : null;
    trackContentClicked(amplitudeType, item.content_id || item.id || null);

    const body = JSON.stringify({
      recId: item.recId ?? null,
      contentId: item.content_id || item.id || null,
      action: 'click',
    });
    const url = '/api/chat/recommendation-event';
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body,
      }).catch(() => {});
    }
  } catch {
    // Never let click instrumentation break navigation.
  }
}

function formatDuration(seconds?: number): string | null {
  if (!seconds || seconds <= 0) return null;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
}

export const ContentRecommendations: React.FC<ContentRecommendationsProps> = ({
  recommendations,
}) => {
  const { t } = useTranslation();

  const getContentTypeName = (contentType?: string): string => {
    if (!contentType) return t('content.meditation');

    switch (contentType.toLowerCase()) {
      case 'meditation':
        return t('content.meditation');
      case 'blog':
        return t('content.article');
      case 'music':
        return t('content.music');
      case 'story':
        return t('content.story');
      case 'theme':
        return t('content.theme');
      case 'playlist':
        return t('content.playlist');
      case 'category':
        return t('content.category');
      case 'yoga':
        return t('content.yoga');
      default:
        return contentType.toUpperCase();
    }
  };

  return (
    <Container>
      <ScrollContainer>
        {recommendations.map((item) => {
          const duration = formatDuration(item.duration);
          const link = withPresentOverWebView(toUniversalLink(item.url));

          return (
            <Card
              key={item.id}
              href={link || '#'}
              onClick={(e) => {
                if (!link) {
                  e.preventDefault();
                }
                recordRecommendationClick(item);
              }}
            >
              <ImageContainer>
                <Image
                  src={item.image || '/img/content-placeholder.jpg'}
                  alt={item.title}
                  onError={(e) => {
                    e.currentTarget.src = '/img/content-placeholder.jpg';
                  }}
                />
                <Badge>{getContentTypeName(item.content_type)}</Badge>
                {item.isPlayable && <PlayButton />}
                {duration && <DurationBadge>{duration}</DurationBadge>}
              </ImageContainer>

              <CardTitle>{item.title}</CardTitle>
              {item.subtitle && <CardSubtitle>{item.subtitle}</CardSubtitle>}
            </Card>
          );
        })}
      </ScrollContainer>
    </Container>
  );
};
